import React, { useEffect, useState } from 'react';
import { CreditCard, Check } from 'lucide-react';
import PaymentModeSelector from './PaymentModeSelector';
import BankDetailsForm from './BankDetailsForm';
import { type PaymentModeType, type BankDetails } from './types';
import { loadDevJson, saveDevJson } from '../../../lib/devStorage';

const PAYMENT_SETTINGS_KEY = 'payment_settings_v1';

type PaymentSettingsState = {
  paymentMode: PaymentModeType;
  bankDetails: BankDetails;
};

const emptyBank: BankDetails = { accountName: '', accountNumber: '', bankName: '', ifscCode: '' };

const PaymentSettings: React.FC = () => {
  const initial = loadDevJson<PaymentSettingsState>(PAYMENT_SETTINGS_KEY, {
    paymentMode: 'offline',
    bankDetails: emptyBank,
  });
  const [paymentMode, setPaymentMode] = useState<PaymentModeType>(initial.paymentMode || 'offline');
  const [bankDetails, setBankDetails] = useState<BankDetails>({ ...emptyBank, ...(initial.bankDetails || {}) });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [saved]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const keepBank = paymentMode === 'online' || paymentMode === 'both';
    saveDevJson(PAYMENT_SETTINGS_KEY, {
      paymentMode,
      bankDetails: keepBank ? bankDetails : emptyBank,
    });
    setSaved(true);
  };

  const showBankDetails = paymentMode === 'online' || paymentMode === 'both';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-zinc-900">
        <CreditCard className="h-5 w-5 text-emerald-700" aria-hidden />
        <p className="text-sm text-zinc-600">Choose how repayments and payouts are recorded.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <PaymentModeSelector paymentMode={paymentMode} setPaymentMode={setPaymentMode} />

        {showBankDetails && <BankDetailsForm bankDetails={bankDetails} setBankDetails={setBankDetails} />}

        <div className="flex items-center justify-end gap-3 border-t border-zinc-100 pt-4">
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
              <Check className="h-4 w-4" /> Saved
            </span>
          )}
          <button type="submit" className="app-btn-primary">
            Save payment settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentSettings;
